import {
  decodeReplay,
  joinReplays,
  type CommentInput,
  type CommentUpdate,
  type DemoComment,
  type CreateMatchInput,
  type DemoCheck,
  type DemoDetail,
  type DemoScope,
  type DemoUploadInput,
  type MatchDetail,
  type MatchNote,
  type MatchSummary,
  type Replay,
  type UpdateMatchInput,
  type VoiceTrack,
  type VoiceTrackUpdate,
} from "@69ly/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { gunzip } from "../demo/unpack.ts";
import { api, ApiFailure } from "./api.ts";

export const matchKeys = {
  all: ["matches"] as const,
  list: (scope: string) => ["matches", "list", scope] as const,
  one: (id: string) => ["matches", "one", id] as const,
  demo: (id: string) => ["matches", "demo", id] as const,
  comments: (id: string) => ["matches", "comments", id] as const,
  notes: (id: string) => ["matches", "notes", id] as const,
  voices: (id: string) => ["matches", "voices", id] as const,
};
const replayKey = (id: string) => ["replays", id] as const;

/** A library's matches: a team's (by id) or Pro matches, newest first, each with its maps. */
export function useMatches(scope: DemoScope | null) {
  return useQuery({
    queryKey: matchKeys.list(scope ?? ""),
    queryFn: () => api<MatchSummary[]>("GET", `/matches?scope=${encodeURIComponent(scope!)}`),
    enabled: !!scope,
  });
}

export function useMatch(id: string | undefined) {
  return useQuery({ queryKey: matchKeys.one(id ?? ""), queryFn: () => api<MatchDetail>("GET", `/matches/${id}`), enabled: !!id });
}

/** One map, with its match, as the viewer opens it. */
export function useDemo(id: string | undefined) {
  return useQuery({ queryKey: matchKeys.demo(id ?? ""), queryFn: () => api<DemoDetail>("GET", `/demos/${id}`), enabled: !!id });
}

async function fetchReplay(url: string): Promise<Replay> {
  const res = await fetch(url, { credentials: "same-origin" });
  if (!res.ok) throw new ApiFailure(res.status, res.status === 404 ? "not_found" : "internal", res.statusText);
  return decodeReplay(await gunzip(await res.blob()));
}

/**
 * The replay, downloaded, unpacked and checked; a relaunched map's demos joined into
 * one. Its address changes when it is read again: kept for the session, so going back
 * to a map opens at once.
 */
export function useReplay(demo: Pick<DemoDetail, "id" | "replayUrl" | "parts"> | undefined) {
  const urls = demo ? (demo.parts?.length ? demo.parts.map((p) => p.replayUrl) : [demo.replayUrl]) : [];
  return useQuery({
    queryKey: [...replayKey(demo?.id ?? ""), ...urls],
    enabled: !!demo,
    staleTime: Infinity,
    gcTime: 10 * 60_000,
    queryFn: async (): Promise<Replay> => {
      const parts = await Promise.all(urls.map(fetchReplay));
      if (parts.length === 1) return parts[0];
      try {
        return joinReplays(parts);
      } catch (error) {
        // Parts that do not fit together: the first is still worth watching.
        console.warn("69LY: the demos of this map could not be joined", error);
        return parts[0];
      }
    },
  });
}

export const checkDemo = (scope: DemoScope, fingerprint: string) => api<DemoCheck>("GET", `/demos/check?scope=${encodeURIComponent(scope)}&fingerprint=${fingerprint}`);

export const createMatch = (input: CreateMatchInput) => api<MatchSummary>("POST", "/matches", input);

/** Sends a map's replay into a match; progress from 0 to 1. */
export function uploadDemo(meta: DemoUploadInput, replay: Blob, onProgress: (done: number) => void): Promise<DemoDetail> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/demos");
    xhr.withCredentials = true;
    xhr.responseType = "json";
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onerror = () => reject(new ApiFailure(0, "network", "Network error"));
    xhr.onload = () => {
      const data = xhr.response as { code?: ApiFailure["code"]; message?: string } | null;
      if (xhr.status >= 200 && xhr.status < 300) return resolve(data as unknown as DemoDetail);
      reject(new ApiFailure(xhr.status, data?.code ?? "internal", data?.message ?? xhr.statusText));
    };
    const form = new FormData();
    form.append("meta", JSON.stringify(meta));
    form.append("replay", replay, "replay.bin.gz");
    xhr.send(form);
  });
}

function useMatchMutation<I, O>(fn: (input: I) => Promise<O>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: matchKeys.all }) });
}

export const useUpdateMatch = (id: string) => useMatchMutation((input: UpdateMatchInput) => api<MatchSummary>("PATCH", `/matches/${id}`, input));
export const useDeleteMatch = () => useMatchMutation((id: string) => api<void>("DELETE", `/matches/${id}`));
export const useMergeMatch = (id: string) => useMatchMutation((from: string) => api<MatchSummary>("POST", `/matches/${id}/merge`, { from }));
export const useSplitDemo = () => useMatchMutation((demoId: string) => api<MatchSummary>("POST", `/demos/${demoId}/split`));
export const useJoinDemo = () => useMatchMutation((demoId: string) => api<MatchSummary>("POST", `/demos/${demoId}/join`));
export const useSeparateDemo = () => useMatchMutation((demoId: string) => api<MatchSummary>("POST", `/demos/${demoId}/separate`));

/** Reads a demo again: its new replay replaces the one kept. */
export async function replaceReplay(demoId: string, replay: Blob): Promise<DemoDetail> {
  const form = new FormData();
  form.append("replay", replay, "replay.bin.gz");
  const res = await fetch(`/api/demos/${demoId}/replay`, { method: "PUT", body: form, credentials: "same-origin" });
  const data = (await res.json().catch(() => null)) as { code?: ApiFailure["code"]; message?: string } | null;
  if (!res.ok) throw new ApiFailure(res.status, data?.code ?? "internal", data?.message ?? res.statusText);
  return data as unknown as DemoDetail;
}

export const useDeleteDemo = () => useMatchMutation((demoId: string) => api<void>("DELETE", `/demos/${demoId}`));

/** Every note of a match, for its page. */
export const useMatchNotes = (matchId: string) => useQuery({ queryKey: matchKeys.notes(matchId), queryFn: () => api<MatchNote[]>("GET", `/matches/${matchId}/notes`) });

/** Notes pinned to moments of a map. */
export const useComments = (demoId: string) => useQuery({ queryKey: matchKeys.comments(demoId), queryFn: () => api<DemoComment[]>("GET", `/demos/${demoId}/comments`) });

export function useAddComment(demoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CommentInput) => api<DemoComment>("POST", `/demos/${demoId}/comments`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: matchKeys.comments(demoId) });
      void qc.invalidateQueries({ queryKey: ["matches", "notes"] });
    },
  });
}

/** A note changed by its author. */
export function useEditComment(demoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...change }: CommentUpdate & { id: string }) => api<DemoComment>("PATCH", `/demos/${demoId}/comments/${id}`, change),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: matchKeys.comments(demoId) });
      void qc.invalidateQueries({ queryKey: ["matches", "notes"] });
    },
  });
}

export function useDeleteComment(demoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => api<void>("DELETE", `/demos/${demoId}/comments/${commentId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: matchKeys.comments(demoId) });
      void qc.invalidateQueries({ queryKey: ["matches", "notes"] });
    },
  });
}

// ---------------------------------------------------------------- voice

/** The voice tracks of a map. */
export const useVoices = (demoId: string) => useQuery({ queryKey: matchKeys.voices(demoId), queryFn: () => api<VoiceTrack[]>("GET", `/demos/${demoId}/voices`) });

export const voiceAudioUrl = (demoId: string, voiceId: string) => `/api/demos/${demoId}/voices/${voiceId}/audio`;

/** Adds an audio file to a map; progress from 0 to 1. */
export function uploadVoice(demoId: string, file: File, label: string, onProgress: (done: number) => void): Promise<VoiceTrack> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/demos/${demoId}/voices?label=${encodeURIComponent(label)}`);
    xhr.withCredentials = true;
    xhr.responseType = "json";
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onerror = () => reject(new ApiFailure(0, "network", "Network error"));
    xhr.onload = () => {
      const data = xhr.response as { code?: ApiFailure["code"]; message?: string } | null;
      if (xhr.status >= 200 && xhr.status < 300) return resolve(data as unknown as VoiceTrack);
      reject(new ApiFailure(xhr.status, data?.code ?? "internal", data?.message ?? xhr.statusText));
    };
    const form = new FormData();
    form.append("file", file, file.name);
    xhr.send(form);
  });
}

export function useUpdateVoice(demoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: VoiceTrackUpdate & { id: string }) => api<VoiceTrack>("PATCH", `/demos/${demoId}/voices/${id}`, input),
    // Lined up at once: the voice moves as the button is pressed, the answer confirms it.
    onMutate: async ({ id, ...input }) => {
      await qc.cancelQueries({ queryKey: matchKeys.voices(demoId) });
      const before = qc.getQueryData<VoiceTrack[]>(matchKeys.voices(demoId));
      qc.setQueryData<VoiceTrack[]>(matchKeys.voices(demoId), (list) =>
        list?.map((v) => (v.id === id ? { ...v, ...(input.label !== undefined && { label: input.label.trim() }), ...(input.offsets && { offsets: input.offsets }) } : v)),
      );
      return { before };
    },
    onError: (_e, _v, ctx) => ctx?.before && qc.setQueryData(matchKeys.voices(demoId), ctx.before),
    onSuccess: (track) => qc.setQueryData<VoiceTrack[]>(matchKeys.voices(demoId), (list) => list?.map((v) => (v.id === track.id ? track : v))),
  });
}

export function useDeleteVoice(demoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>("DELETE", `/demos/${demoId}/voices/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: matchKeys.voices(demoId) }),
  });
}
